import { Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DatePicker } from '@/components/ui/date-picker'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import type { EntryDraft } from './scheduleDraft'

interface ScheduleEntryRowsProps {
  entries: readonly EntryDraft[]
  onChange: (entries: EntryDraft[]) => void
  /** A proposal's own schedule can fall due on a fixed day; a saved one can't. */
  allowDates?: boolean
  /** A change to a signed agreement: its deposit is paid, so its first payment isn't one. */
  noDeposit?: boolean
}

function dueKindOf(value: string): EntryDraft['dueKind'] {
  if (value === 'before_event' || value === 'on_date') return value
  return 'on_signing'
}

/** The payments in a schedule, one row each: what it's called, how much, and when it's due. */
export function ScheduleEntryRows({ entries, onChange, allowDates = false, noDeposit = false }: ScheduleEntryRowsProps) {
  const update = (key: string, patch: Partial<EntryDraft>): void => onChange(entries.map((e) => (e.key === key ? { ...e, ...patch } : e)))
  return (
    <ol className="m-0 list-none space-y-2 p-0">
      {entries.map((entry, index) => (
        <li key={entry.key} className="flex flex-wrap items-center gap-2">
          <span className="w-16 text-xs text-ink-muted">{index === 0 && !noDeposit ? 'Deposit' : `Payment ${String(index + 1)}`}</span>
          <Input size="sm" aria-label="Payment name" className="w-40" value={entry.label} onChange={(e) => update(entry.key, { label: e.target.value })} />
          <div className="flex items-center gap-2">
            <Select
              size="sm"
              aria-label="Amount"
              value={entry.amountKind}
              onChange={(e) => {
                const kind = e.target.value
                if (kind === 'fixed' || kind === 'percent' || kind === 'rest') update(entry.key, { amountKind: kind, amount: '' })
              }}
            >
              <option value="percent">A percentage</option>
              <option value="fixed">A fixed amount</option>
              <option value="rest">The rest</option>
            </Select>
            {entry.amountKind === 'rest' ? null : (
              <div className="flex items-center gap-1">
                {entry.amountKind === 'fixed' ? <span className="text-sm text-ink-muted">$</span> : null}
                <Input
                  size="sm"
                  inputMode="decimal"
                  aria-label={entry.amountKind === 'fixed' ? 'Dollars' : 'Percent'}
                  className="w-24 text-right tabular-nums"
                  value={entry.amount}
                  onChange={(e) => update(entry.key, { amount: e.target.value })}
                />
                {entry.amountKind === 'percent' ? <span className="text-sm text-ink-muted">%</span> : null}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Select size="sm" aria-label="Due" value={entry.dueKind} onChange={(e) => update(entry.key, { dueKind: dueKindOf(e.target.value) })}>
              <option value="on_signing">Due on signing</option>
              <option value="before_event">Due before the event</option>
              {allowDates ? <option value="on_date">Due on a date</option> : null}
            </Select>
            {entry.dueKind === 'before_event' ? (
              <div className="flex items-center gap-1">
                <Input
                  size="sm"
                  inputMode="numeric"
                  aria-label="Days before the event"
                  className="w-16 text-right tabular-nums"
                  value={entry.days}
                  onChange={(e) => update(entry.key, { days: e.target.value })}
                />
                <span className="text-sm text-ink-muted">days</span>
              </div>
            ) : null}
            {entry.dueKind === 'on_date' ? <DatePicker value={entry.date} onChange={(date) => update(entry.key, { date })} className="h-8" /> : null}
          </div>
          <Button variant="ghost" size="sm" aria-label="Remove this payment" onClick={() => onChange(entries.filter((e) => e.key !== entry.key))}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </li>
      ))}
    </ol>
  )
}
